import Image from "next/image";
import Link from "next/link";
import { BLOCKS, INLINES, MARKS } from "@contentful/rich-text-types";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";

import styles from "./rich-text.module.scss";
import clsx from "clsx";

const CUSTOM_ASSET_GROUP = "asset-group";
const CUSTOM_PARAGRAPH_GROUP_EVEN = "paragraph-group-even";
const CUSTOM_PARAGRAPH_GROUP_ODD = "paragraph-group-odd";

function newContentGroup(nodeType) {
  return {
    nodeType,
    content: []
  };
}

function createParagraphGroups(content) {
  let count = 0;
  let lastParagraphGroup = newContentGroup(CUSTOM_PARAGRAPH_GROUP_EVEN);

  let newContent = content.json.content.reduce((newContent, item) => {
    const paragraphBlocks = [
      BLOCKS.HEADING_1,
      BLOCKS.HEADING_2,
      BLOCKS.HEADING_3,
      BLOCKS.HEADING_4,
      BLOCKS.HEADING_5,
      BLOCKS.HEADING_6,
      BLOCKS.PARAGRAPH,
      BLOCKS.UL_LIST,
      BLOCKS.OL_LIST,
      BLOCKS.HR
    ];
    if (paragraphBlocks.includes(item.nodeType)) {
      lastParagraphGroup.content.push(item);
    } else {
      // check last paragraph group, push and reset
      count++;
      if (lastParagraphGroup.content.length > 0) {
        newContent.push(lastParagraphGroup);
        if (count % 2 === 0) {
          lastParagraphGroup = newContentGroup(CUSTOM_PARAGRAPH_GROUP_EVEN);
        } else {
          lastParagraphGroup = newContentGroup(CUSTOM_PARAGRAPH_GROUP_ODD);
        }
      }

      newContent.push(item);
    }

    return newContent;
  }, []);

  // Push final paragraph group into content.
  if (lastParagraphGroup.content.length > 0) {
    newContent.push(lastParagraphGroup);
  }

  return newContent;
}

function createAssetGroups(content) {
  let lastAssetGroup = newContentGroup(CUSTOM_ASSET_GROUP);

  return content.json.content.reduce((newContent, item) => {
    if (item.nodeType === BLOCKS.EMBEDDED_ASSET) {
      // Push into last asset group
      lastAssetGroup.content.push(item);
    } else {
      // Check last asset group length, push and reset.
      if (lastAssetGroup.content.length > 0) {
        newContent.push(lastAssetGroup);
        lastAssetGroup = newContentGroup(CUSTOM_ASSET_GROUP);
      }

      newContent.push(item);
    }

    return newContent;
  }, []);
}

export default function RichText({ content }) {
  const assets = {};

  for (const item of content?.links?.assets?.block || []) {
    assets[item.sys?.id] = item;
  }

  if (content && content.json && content.json.content) {
    content.json.content = createAssetGroups(content);
    content.json.content = createParagraphGroups(content);
  }

  const baseNodes = {
    [BLOCKS.HEADING_1]: (node, children) => <h1>{children}</h1>,
    [BLOCKS.HEADING_2]: (node, children) => <h2>{children}</h2>,
    [BLOCKS.HEADING_3]: (node, children) => <h3>{children}</h3>,
    [BLOCKS.PARAGRAPH]: (node, children) => <p>{children}</p>,
    [BLOCKS.UL_LIST]: (node, children) => (
      <ul className={styles.richTextUl}>{children}</ul>
    ),
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      const { title, description, contentType, url } =
        assets[node.data.target.sys.id];

      switch (contentType.split("/")[0]) {
        case "image":
          return (
            <div className={styles.assetWrapper}>
              <figure>
                <Image
                  className={styles.asset}
                  title={title}
                  alt={description}
                  src={url}
                  layout="fill"
                  objectFit="contain"
                />
                <figcaption>{description}</figcaption>
              </figure>
            </div>
          );
        default:
          return <span>[{contentType} embedded asset]</span>;
      }
    },
    [INLINES.HYPERLINK]: (node, children) => {
      return (
        <Link href={node.data.uri} passHref>
          <a
            className={styles.anchor}
            target="_blank"
            rel="noopener noreferrer"
          >
            {children}
          </a>
        </Link>
      );
    },
    [MARKS.BOLD]: (text) => <b key={`${text}-key`}>{text}</b>,
    [MARKS.ITALIC]: (text) => <i key={`${text}-key`}>{text}</i>,
    [MARKS.UNDERLINE]: (text) => <u key={`${text}-key`}>{text}</u>
  };

  const options = {
    renderMark: {},
    renderNode: {
      ...baseNodes,
      [CUSTOM_ASSET_GROUP]: (node) => {
        return (
          <div className={styles.assetGroup} data-images={node.content.length}>
            {node.content.map((assetNode) => {
              const { title, description, url } =
                assets[assetNode.data.target.sys.id];
              return (
                <figure
                  className={styles.asset}
                  key={assetNode.data.target.sys.id}
                >
                  <img
                    className={styles.asset}
                    title={title}
                    alt={description}
                    src={url}
                  />
                  <figcaption>{description}</figcaption>
                </figure>
              );
            })}
          </div>
        );
      },
      [CUSTOM_PARAGRAPH_GROUP_EVEN]: (node) => {
        return (
          <div
            className={clsx(styles.paragraphGroup, styles.paragraphGroupEven)}
          >
            {documentToReactComponents(node)}
          </div>
        );
      },
      [CUSTOM_PARAGRAPH_GROUP_ODD]: (node) => {
        return (
          <div
            className={clsx(styles.paragraphGroup, styles.paragraphGroupOdd)}
          >
            {documentToReactComponents(node)}
          </div>
        );
      }
    }
  };

  return (
    <div className={styles.richText}>
      {documentToReactComponents(content?.json, options)}
    </div>
  );
}
